import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { getCampaigns } from '@/api/campaigns';
import { Select } from '@/components/Select';
import { TrackerLinkPicker } from '@/components/TrackerLinkPicker';
import {
  generateNative,
  regenerateText,
  regenerateIcon,
  bulkCreateAds,
  getGeoMap,
  generatePopup,
  regeneratePopupText,
  bulkCreatePopupAds,
  type GeneratedVariation,
  type GenerateNativeRequest,
  type GeneratedPopupVariation,
  type GeneratePopupRequest,
} from '@/api/generate';

const LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ar', name: 'Arabic' },
  { code: 'tr', name: 'Turkish' },
  { code: 'pl', name: 'Polish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'th', name: 'Thai' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ms', name: 'Malay' },
  { code: 'cs', name: 'Czech' },
  { code: 'ro', name: 'Romanian' },
  { code: 'el', name: 'Greek' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'da', name: 'Danish' },
  { code: 'fi', name: 'Finnish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'hr', name: 'Croatian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'he', name: 'Hebrew' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'et', name: 'Estonian' },
  { code: 'fa', name: 'Persian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'sw', name: 'Swahili' },
  { code: 'am', name: 'Amharic' },
  { code: 'ka', name: 'Georgian' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'sq', name: 'Albanian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'my', name: 'Burmese' },
  { code: 'km', name: 'Khmer' },
  { code: 'lo', name: 'Lao' },
  { code: 'ne', name: 'Nepali' },
  { code: 'si', name: 'Sinhala' },
  { code: 'so', name: 'Somali' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'tg', name: 'Tajik' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'ps', name: 'Pashto' },
  { code: 'bn', name: 'Bengali' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'dv', name: 'Dhivehi' },
];


const ICON_STYLES = [
  { value: 'appstore', label: 'App Store' },
  { value: 'realistic', label: 'Realistic' },
  { value: 'minimal', label: 'Minimalist' },
  { value: 'bright', label: 'Bright & Bold' },
];

type Step = 'config' | 'preview' | 'create';
type GenerateMode = 'native' | 'popup';

export function Generate() {
  const [mode, setMode] = useState<GenerateMode>('native');
  const [step, setStep] = useState<Step>('config');

  // Config state
  const [vertical, setVertical] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [count, setCount] = useState(5);
  const [generateIcons, setGenerateIcons] = useState(true);
  const [generateTitles, setGenerateTitles] = useState(true);
  const [generateBodies, setGenerateBodies] = useState(true);
  const [generateCta, setGenerateCta] = useState(true);
  const [iconStyle, setIconStyle] = useState('appstore');
  const [iconPrompt, setIconPrompt] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['android', 'ios']);

  // Popup-specific config
  const [generateButton2, setGenerateButton2] = useState(true);
  const [popupDelaySec, setPopupDelaySec] = useState('0');
  const [popupRepeatSec, setPopupRepeatSec] = useState('');

  // Preview state
  const [variations, setVariations] = useState<GeneratedVariation[]>([]);
  const [popupVariations, setPopupVariations] = useState<GeneratedPopupVariation[]>([]);
  const [selectedVariations, setSelectedVariations] = useState<Set<string>>(new Set());
  const [editingField, setEditingField] = useState<{ id: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [regenerating, setRegenerating] = useState<Set<string>>(new Set());

  // Create state
  const [campaignId, setCampaignId] = useState('');
  const [clickUrl, setClickUrl] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<{ created: number } | null>(null);

  // Loading
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Queries
  const { data: geoMap } = useQuery({ queryKey: ['geo-map'], queryFn: getGeoMap });
  const { data: campaigns } = useQuery({ queryKey: ['campaigns'], queryFn: getCampaigns });

  const effectiveVertical = vertical;

  // Get GEOs for selected languages
  const getGeosForLanguages = useCallback(() => {
    if (!geoMap) return {};
    const result: Record<string, string[]> = {};
    for (const lang of selectedLanguages) {
      result[lang] = geoMap[lang] || [];
    }
    return result;
  }, [geoMap, selectedLanguages]);

  const totalGeos = useCallback(() => {
    const langGeos = getGeosForLanguages();
    const all = new Set<string>();
    Object.values(langGeos).forEach(geos => geos.forEach(g => all.add(g)));
    return all;
  }, [getGeosForLanguages]);

  // Handle generate
  const handleGenerate = async () => {
    if (!effectiveVertical || !selectedLanguages.length) {
      setError('Select vertical and at least one language');
      return;
    }
    if (!generateIcons && !generateTitles && !generateBodies && !generateCta) {
      setError('Select at least one element to generate');
      return;
    }

    setError('');
    setGenerating(true);

    try {
      const request: GenerateNativeRequest = {
        vertical: effectiveVertical,
        languages: selectedLanguages,
        count,
        generate_icons: generateIcons,
        generate_titles: generateTitles,
        generate_bodies: generateBodies,
        generate_cta: generateCta,
        icon_style: iconStyle,
        icon_prompt: iconPrompt || undefined,
        platforms,
      };

      const result = await generateNative(request);
      setVariations(result);
      setSelectedVariations(new Set(result.map(v => v.id)));
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Handle popup generate
  const handleGeneratePopup = async () => {
    if (!effectiveVertical || !selectedLanguages.length) {
      setError('Select vertical and at least one language');
      return;
    }
    if (!clickUrl) {
      setError('Enter click URL for popup buttons');
      return;
    }
    if (!generateTitles && !generateBodies && !generateCta && !generateButton2) {
      setError('Select at least one element to generate');
      return;
    }

    setError('');
    setGenerating(true);

    try {
      const request: GeneratePopupRequest = {
        vertical: effectiveVertical,
        languages: selectedLanguages,
        count,
        generate_titles: generateTitles,
        generate_subtitles: generateBodies,
        generate_button1: generateCta,
        generate_button2: generateButton2,
        platforms,
      };

      const result = await generatePopup(request);
      setPopupVariations(result);
      setSelectedVariations(new Set(result.map(v => v.id)));
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Handle regenerate popup text
  const handleRegeneratePopupText = async (id: string, field: 'ad_text' | 'ad_body' | 'cta_text' | 'cta_text_2') => {
    const variation = popupVariations.find(v => v.id === id);
    if (!variation) return;

    const regenKey = `${id}-${field}`;
    setRegenerating(prev => new Set(prev).add(regenKey));

    try {
      const result = await regeneratePopupText({
        vertical: effectiveVertical,
        language: variation.language,
        geos: variation.geos,
        field,
        platforms,
      });

      setPopupVariations(prev =>
        prev.map(v => v.id === id ? { ...v, [field]: result.text } : v)
      );
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(prev => {
        const next = new Set(prev);
        next.delete(regenKey);
        return next;
      });
    }
  };

  // Handle popup bulk create
  const handlePopupBulkCreate = async () => {
    if (!campaignId || !clickUrl) return;

    setCreating(true);
    try {
      const selected = popupVariations.filter(v => selectedVariations.has(v.id));
      const result = await bulkCreatePopupAds({
        campaign_id: campaignId,
        click_url: clickUrl,
        popup_delay_sec: Number(popupDelaySec) || 0,
        popup_repeat_sec: Number(popupRepeatSec) || 0,
        platforms,
        variations: selected.map(({ language, geos, ad_text, ad_body, cta_text, cta_text_2 }) => ({
          language, geos, ad_text, ad_body, cta_text, cta_text_2,
        })),
      });

      setCreateResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to create ads');
    } finally {
      setCreating(false);
    }
  };

  // Handle field edit
  const startEdit = (id: string, field: string, currentValue: string) => {
    setEditingField({ id, field });
    setEditValue(currentValue || '');
  };

  const saveEdit = () => {
    if (!editingField) return;
    if (mode === 'popup') {
      setPopupVariations(prev =>
        prev.map(v =>
          v.id === editingField.id ? { ...v, [editingField.field]: editValue } : v
        )
      );
    } else {
      setVariations(prev =>
        prev.map(v =>
          v.id === editingField.id ? { ...v, [editingField.field]: editValue } : v
        )
      );
    }
    setEditingField(null);
  };

  // Handle regenerate single field
  const handleRegenerateText = async (id: string, field: 'ad_text' | 'ad_body' | 'cta_text') => {
    const variation = variations.find(v => v.id === id);
    if (!variation) return;

    const regenKey = `${id}-${field}`;
    setRegenerating(prev => new Set(prev).add(regenKey));

    try {
      const result = await regenerateText({
        vertical: effectiveVertical,
        language: variation.language,
        geos: variation.geos,
        field,
        platforms,
      });

      setVariations(prev =>
        prev.map(v => v.id === id ? { ...v, [field]: result.text } : v)
      );
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(prev => {
        const next = new Set(prev);
        next.delete(regenKey);
        return next;
      });
    }
  };

  const handleRegenerateIcon = async (id: string) => {
    const regenKey = `${id}-icon`;
    setRegenerating(prev => new Set(prev).add(regenKey));

    try {
      const result = await regenerateIcon({
        vertical: effectiveVertical,
        icon_style: iconStyle,
        icon_prompt: iconPrompt || undefined,
      });

      setVariations(prev =>
        prev.map(v => v.id === id ? { ...v, icon_url: result.icon_url } : v)
      );
    } catch (err) {
      console.error('Regenerate icon failed:', err);
    } finally {
      setRegenerating(prev => {
        const next = new Set(prev);
        next.delete(regenKey);
        return next;
      });
    }
  };

  // Toggle variation selection
  const toggleVariation = (id: string) => {
    setSelectedVariations(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVariations = () => {
    const currentVars = mode === 'popup' ? popupVariations : variations;
    if (selectedVariations.size === currentVars.length) {
      setSelectedVariations(new Set());
    } else {
      setSelectedVariations(new Set(currentVars.map(v => v.id)));
    }
  };

  // Handle bulk create
  const handleBulkCreate = async () => {
    if (!campaignId || !clickUrl) return;

    setCreating(true);
    try {
      const selected = variations.filter(v => selectedVariations.has(v.id));
      const result = await bulkCreateAds({
        campaign_id: campaignId,
        click_url: clickUrl,
        platforms,
        variations: selected.map(({ language, geos, ad_text, ad_body, cta_text, icon_url }) => ({
          language, geos, ad_text, ad_body, cta_text, icon_url,
        })),
      });

      setCreateResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to create ads');
    } finally {
      setCreating(false);
    }
  };

  const langName = (code: string) => LANGUAGES.find(l => l.code === code)?.name || code;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Generate Ads</h1>
          <p className="mt-1 text-sm text-content-subtle">
            AI-powered ad generation with Nano Banana and GPT-4o
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {(['config', 'preview', 'create'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-primary text-white'
                    : steps(step) > i
                      ? 'bg-primary/10 text-primary'
                      : 'bg-surface-muted text-content-subtle'
                }`}
              >
                {i + 1}
              </div>
              {i < 2 && <div className="w-8 h-px bg-border-strong mx-1" />}
            </div>
          ))}
        </div>
      </div>

      {/* Mode Tabs */}
      {step === 'config' && (
        <div className="flex gap-1 mb-6 bg-surface-muted rounded-lg p-1 w-fit">
          <button
            onClick={() => setMode('native')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'native'
                ? 'bg-surface text-primary shadow-sm'
                : 'text-content-subtle hover:text-content-muted'
            }`}
          >
            Custom Banner
          </button>
          <button
            onClick={() => setMode('popup')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              mode === 'popup'
                ? 'bg-surface text-primary shadow-sm'
                : 'text-content-subtle hover:text-content-muted'
            }`}
          >
            Popup Ads
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-danger-soft border border-danger/30 text-danger rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Config */}
      {step === 'config' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Main settings */}
          <div className="space-y-5">
            <div className="bg-surface rounded-lg border border-border p-5">
              <h3 className="text-sm font-medium text-content mb-4">Settings</h3>

              {/* Vertical */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-content-muted mb-1">Theme / Description</label>
                <textarea
                  value={vertical}
                  onChange={e => setVertical(e.target.value)}
                  placeholder="Describe what to generate, e.g.: gambling casino app for high rollers, sports betting with live odds, dating app for 30+..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary resize-none"
                />
              </div>

              {/* Languages */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-content-muted">
                    Languages
                    {selectedLanguages.length > 0 && (
                      <span className="ml-2 text-primary">({selectedLanguages.length} selected)</span>
                    )}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedLanguages.length === LANGUAGES.length) {
                        setSelectedLanguages([]);
                      } else {
                        setSelectedLanguages(LANGUAGES.map(l => l.code));
                      }
                    }}
                    className="text-xs text-primary hover:text-primary font-medium"
                  >
                    {selectedLanguages.length === LANGUAGES.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="border border-border-strong rounded-md max-h-48 overflow-y-auto p-2">
                  {LANGUAGES.map(lang => (
                    <label
                      key={lang.code}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLanguages.includes(lang.code)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedLanguages(prev => [...prev, lang.code]);
                          } else {
                            setSelectedLanguages(prev => prev.filter(l => l !== lang.code));
                          }
                        }}
                        className="rounded border-border-strong text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-content-muted">{lang.name}</span>
                      <span className="text-xs text-content-subtle ml-auto">
                        {geoMap?.[lang.code]?.length || 0} GEOs
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Count */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-content-muted mb-1">
                  Variations per language: <span className="text-primary font-semibold">{count}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={count}
                  onChange={e => setCount(Number(e.target.value))}
                  className="w-full accent-purple-600"
                />
                <div className="flex justify-between text-xs text-content-subtle">
                  <span>1</span>
                  <span>20</span>
                </div>
              </div>

              {/* Platforms */}
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Platforms</label>
                <div className="flex gap-3">
                  {['android', 'ios'].map(p => (
                    <label key={p} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={platforms.includes(p)}
                        onChange={e => {
                          if (e.target.checked) {
                            setPlatforms(prev => [...prev, p]);
                          } else {
                            setPlatforms(prev => prev.filter(x => x !== p));
                          }
                        }}
                        className="rounded border-border-strong text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-content-muted capitalize">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate elements */}
            <div className="bg-surface rounded-lg border border-border p-5">
              <h3 className="text-sm font-medium text-content mb-4">What to generate</h3>
              <div className="space-y-3">
                {mode === 'native' && (
                  <>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={generateIcons}
                        onChange={e => setGenerateIcons(e.target.checked)}
                        className="rounded border-border-strong text-primary focus:ring-primary"
                      />
                      <div>
                        <span className="text-sm font-medium text-content-muted">Icons</span>
                        <span className="text-xs text-content-subtle ml-2">Nano Banana (Gemini)</span>
                      </div>
                    </label>

                    {generateIcons && (
                      <div className="ml-7 mb-1">
                        <label className="block text-xs font-medium text-content-subtle mb-1">Icon style</label>
                        <div className="flex gap-2">
                          {ICON_STYLES.map(s => (
                            <button
                              key={s.value}
                              onClick={() => setIconStyle(s.value)}
                              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                                iconStyle === s.value
                                  ? 'border-primary bg-primary/10 text-primary'
                                  : 'border-border text-content-muted hover:border-border-strong'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        <label className="block text-xs font-medium text-content-subtle mt-2 mb-1">Icon description</label>
                        <textarea
                          value={iconPrompt}
                          onChange={e => setIconPrompt(e.target.value)}
                          placeholder="Describe the icon, e.g.: golden crown on dark blue background, slot machine with coins, red heart with flames..."
                          rows={2}
                          className="w-full px-2 py-1.5 border border-border-strong rounded-md text-xs focus:ring-primary focus:border-primary resize-none"
                        />
                      </div>
                    )}
                  </>
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateTitles}
                    onChange={e => setGenerateTitles(e.target.checked)}
                    className="rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-content-muted">Titles</span>
                    <span className="text-xs text-content-subtle ml-2">{mode === 'popup' ? 'Popup title, max 60 chars' : 'Headline, max 50 chars'}</span>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateBodies}
                    onChange={e => setGenerateBodies(e.target.checked)}
                    className="rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-content-muted">Subtitles</span>
                    <span className="text-xs text-content-subtle ml-2">{mode === 'popup' ? 'Popup subtitle, max 100 chars' : 'Body text, max 90 chars'}</span>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateCta}
                    onChange={e => setGenerateCta(e.target.checked)}
                    className="rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <div>
                    <span className="text-sm font-medium text-content-muted">{mode === 'popup' ? 'Button 1' : 'CTA Text'}</span>
                    <span className="text-xs text-content-subtle ml-2">{mode === 'popup' ? 'Primary button, max 25 chars' : 'Button text, max 20 chars'}</span>
                  </div>
                </label>

                {mode === 'popup' && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateButton2}
                      onChange={e => setGenerateButton2(e.target.checked)}
                      className="rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <div>
                      <span className="text-sm font-medium text-content-muted">Button 2</span>
                      <span className="text-xs text-content-subtle ml-2">CTA, different angle, max 25 chars</span>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Popup settings */}
            {mode === 'popup' && (
              <div className="bg-surface rounded-lg border border-border p-5">
                <h3 className="text-sm font-medium text-content mb-4">Popup Settings</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-content-muted mb-1">Delay (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    max={3600}
                    value={popupDelaySec}
                    onChange={e => setPopupDelaySec(e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-content-muted mb-1">Repeat every (seconds)</label>
                  <input
                    type="number"
                    min={0}
                    max={86400}
                    value={popupRepeatSec}
                    onChange={e => setPopupRepeatSec(e.target.value)}
                    placeholder="600"
                    className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                  />
                  <p className="text-xs text-content-subtle mt-1">0 = show immediately</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-muted mb-1">Click URL</label>
                  <div className="flex items-start gap-2">
                    <input
                      type="url"
                      value={clickUrl}
                      onChange={e => setClickUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                    />
                    <div className="w-56 shrink-0">
                      <TrackerLinkPicker onPick={setClickUrl} />
                    </div>
                  </div>
                  <p className="text-xs text-content-subtle mt-1">Link for primary button</p>
                </div>
              </div>
            )}
          </div>

          {/* Right: GEO preview */}
          <div className="space-y-5">
            <div className="bg-surface rounded-lg border border-border p-5">
              <h3 className="text-sm font-medium text-content mb-4">
                GEO Coverage
                <span className="ml-2 text-primary">({totalGeos().size} countries)</span>
              </h3>

              {selectedLanguages.length === 0 ? (
                <p className="text-sm text-content-subtle">Select languages to see target GEOs</p>
              ) : (
                <div className="space-y-3">
                  {selectedLanguages.map(lang => {
                    const geos = geoMap?.[lang] || [];
                    return (
                      <div key={lang} className="border-b border-border pb-3 last:border-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-content-muted">{langName(lang)}</span>
                          <span className="text-xs text-content-subtle">{geos.length} GEOs</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {geos.map(geo => (
                            <span
                              key={geo}
                              className="px-1.5 py-0.5 bg-surface-muted text-content-muted text-xs rounded"
                            >
                              {geo}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bg-primary/10 rounded-lg border border-primary/30 p-5">
              <h3 className="text-sm font-medium text-primary mb-3">Generation Summary</h3>
              <div className="space-y-1 text-sm text-primary">
                <p>Type: <strong>{mode === 'popup' ? 'Popup' : 'Custom Banner'}</strong></p>
                <p>Languages: <strong>{selectedLanguages.length}</strong></p>
                <p>Variations per language: <strong>{count}</strong></p>
                <p>Total variations: <strong>{selectedLanguages.length * count}</strong></p>
                <p>Total GEOs: <strong>{totalGeos().size}</strong></p>
                {mode === 'native' && generateIcons && <p>Icons: <strong>{selectedLanguages.length * count}</strong> (Nano Banana)</p>}
                {mode === 'popup' && <p>Delay: <strong>{popupDelaySec}s</strong>{popupRepeatSec ? <>, Repeat: <strong>{popupRepeatSec}s</strong></> : null}</p>}
              </div>
              {mode === 'native' && generateIcons && selectedLanguages.length * count > 10 && (
                <p className="mt-3 text-xs text-primary">
                  Generating {selectedLanguages.length * count} icons may take a few minutes
                </p>
              )}
            </div>

            <Button
              onClick={mode === 'popup' ? handleGeneratePopup : handleGenerate}
              disabled={generating || !effectiveVertical || !selectedLanguages.length || (mode === 'popup' && !clickUrl)}
              className="w-full"
            >
              {generating ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating...
                </span>
              ) : (
                `Generate ${selectedLanguages.length * count} ${mode === 'popup' ? 'Popup' : 'Custom Banner'} Variations`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && (
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep('config')}
                className="text-sm text-content-subtle hover:text-content-muted flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to settings
              </button>
              <span className="text-sm text-content-subtle">|</span>
              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                {mode === 'popup' ? 'Popup' : 'Custom Banner'}
              </span>
              <span className="text-sm text-content-subtle">|</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedVariations.size === (mode === 'popup' ? popupVariations : variations).length}
                  onChange={toggleAllVariations}
                  className="rounded border-border-strong text-primary focus:ring-primary"
                />
                <span className="text-sm text-content-muted">
                  Select all ({selectedVariations.size}/{(mode === 'popup' ? popupVariations : variations).length})
                </span>
              </label>
            </div>

            <Button
              onClick={() => setShowCreateModal(true)}
              disabled={selectedVariations.size === 0}
            >
              Create {selectedVariations.size} Ads
            </Button>
          </div>

          {/* Native variation cards grouped by language */}
          {mode === 'native' && selectedLanguages.map(lang => {
            const langVars = variations.filter(v => v.language === lang);
            if (langVars.length === 0) return null;

            return (
              <div key={lang} className="mb-6">
                <h3 className="text-sm font-medium text-content mb-3 flex items-center gap-2">
                  {langName(lang)}
                  <span className="text-xs text-content-subtle font-normal">
                    {langVars.filter(v => selectedVariations.has(v.id)).length}/{langVars.length} selected
                  </span>
                  <span className="text-xs text-content-subtle">
                    GEO: {langVars[0]?.geos.join(', ')}
                  </span>
                </h3>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {langVars.map(variation => {
                    const isSelected = selectedVariations.has(variation.id);
                    const hasCta = variation.cta_text !== null && variation.cta_text !== '';
                    const hasBody = variation.ad_body !== null && variation.ad_body !== '';

                    return (
                      <div
                        key={variation.id}
                        className={`rounded-lg overflow-hidden transition-all ${
                          isSelected ? 'ring-2 ring-primary shadow-lg' : 'ring-1 ring-border-strong opacity-60'
                        }`}
                      >
                        {/* SDK-style native ad preview (matches NativeAd.cs: 128px height, 64px icon, 20/15px text) */}
                        <div className="relative" style={{ background: 'rgb(31,31,36)', height: 128 }}>
                          {/* Top accent line */}
                          <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'rgba(77,143,255,0.8)' }} />

                          {/* "Ad" badge */}
                          <div className="absolute top-1.5 right-3.5 px-2 py-0.5 rounded-sm" style={{ background: 'rgba(255,255,255,0.15)' }}>
                            <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Ad</span>
                          </div>

                          {/* Centered content: icon + text + CTA */}
                          <div className="absolute inset-0 flex items-center justify-center" style={{ padding: 14 }}>
                            <div className="flex items-center w-full" style={{ gap: 12, maxWidth: 480 }}>
                              {/* Icon 64×64 */}
                              {variation.icon_url ? (
                                <div className="relative group shrink-0">
                                  <img
                                    src={variation.icon_url}
                                    alt=""
                                    className="rounded-md object-cover"
                                    style={{ width: 64, height: 64 }}
                                  />
                                  <button
                                    onClick={() => handleRegenerateIcon(variation.id)}
                                    disabled={regenerating.has(`${variation.id}-icon`)}
                                    className="absolute inset-0 bg-black/60 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  >
                                    {regenerating.has(`${variation.id}-icon`) ? (
                                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                      </svg>
                                    ) : '↻'}
                                  </button>
                                </div>
                              ) : (
                                <div className="rounded-md shrink-0" style={{ width: 64, height: 64, background: '#333338' }} />
                              )}

                              {/* Text group */}
                              <div className="flex-1 min-w-0 flex flex-col justify-center">
                                {variation.ad_text !== null && (
                                  <div className="truncate font-bold" style={{ fontSize: 20, lineHeight: '24px', color: 'rgba(255,255,255,0.95)' }}>
                                    {variation.ad_text}
                                  </div>
                                )}
                                {hasBody && (
                                  <div className="truncate mt-0.5" style={{ fontSize: 15, lineHeight: '20px', color: 'rgba(255,255,255,0.5)' }}>
                                    {variation.ad_body}
                                  </div>
                                )}
                              </div>

                              {/* CTA or arrow */}
                              {hasCta ? (
                                <div
                                  className="shrink-0 font-bold text-white text-center flex items-center justify-center rounded-sm"
                                  style={{ background: 'rgb(77,143,255)', width: 100, height: 40, fontSize: 16 }}
                                >
                                  {variation.cta_text}
                                </div>
                              ) : (
                                <div className="shrink-0" style={{ fontSize: 28, lineHeight: '24px', color: 'rgba(255,255,255,0.3)' }}>›</div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Edit controls below the preview */}
                        <div className="bg-surface-muted px-3 py-2.5 flex items-start gap-3 border-t border-border">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVariation(variation.id)}
                            className="rounded border-border-strong text-primary focus:ring-primary mt-1"
                          />

                          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                            {variation.ad_text !== null && (
                              <EditableField
                                value={variation.ad_text || ''}
                                isEditing={editingField?.id === variation.id && editingField?.field === 'ad_text'}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onStartEdit={() => startEdit(variation.id, 'ad_text', variation.ad_text || '')}
                                onSave={saveEdit}
                                onCancel={() => setEditingField(null)}
                                onRegenerate={() => handleRegenerateText(variation.id, 'ad_text')}
                                regenerating={regenerating.has(`${variation.id}-ad_text`)}
                                label="Title"
                                className="font-medium text-xs text-content-muted truncate"
                              />
                            )}

                            {variation.ad_body !== null && (
                              <EditableField
                                value={variation.ad_body || ''}
                                isEditing={editingField?.id === variation.id && editingField?.field === 'ad_body'}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onStartEdit={() => startEdit(variation.id, 'ad_body', variation.ad_body || '')}
                                onSave={saveEdit}
                                onCancel={() => setEditingField(null)}
                                onRegenerate={() => handleRegenerateText(variation.id, 'ad_body')}
                                regenerating={regenerating.has(`${variation.id}-ad_body`)}
                                label="Body"
                                className="text-xs text-content-subtle truncate"
                              />
                            )}

                            {variation.cta_text !== null && (
                              <EditableField
                                value={variation.cta_text || ''}
                                isEditing={editingField?.id === variation.id && editingField?.field === 'cta_text'}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onStartEdit={() => startEdit(variation.id, 'cta_text', variation.cta_text || '')}
                                onSave={saveEdit}
                                onCancel={() => setEditingField(null)}
                                onRegenerate={() => handleRegenerateText(variation.id, 'cta_text')}
                                regenerating={regenerating.has(`${variation.id}-cta_text`)}
                                label="CTA"
                                className="text-xs"
                                isCta
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Popup variation cards grouped by language */}
          {mode === 'popup' && selectedLanguages.map(lang => {
            const langVars = popupVariations.filter(v => v.language === lang);
            if (langVars.length === 0) return null;

            return (
              <div key={lang} className="mb-6">
                <h3 className="text-sm font-medium text-content mb-3 flex items-center gap-2">
                  {langName(lang)}
                  <span className="text-xs text-content-subtle font-normal">
                    {langVars.filter(v => selectedVariations.has(v.id)).length}/{langVars.length} selected
                  </span>
                  <span className="text-xs text-content-subtle">
                    GEO: {langVars[0]?.geos.join(', ')}
                  </span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {langVars.map(variation => (
                    <div
                      key={variation.id}
                      className={`bg-surface rounded-lg border-2 p-4 transition-colors ${
                        selectedVariations.has(variation.id)
                          ? 'border-primary/40 shadow-sm'
                          : 'border-border opacity-60'
                      }`}
                    >
                      {/* Popup card - no icon, but has popup-style layout */}
                      <div className="flex items-start gap-3 mb-3">
                        <input
                          type="checkbox"
                          checked={selectedVariations.has(variation.id)}
                          onChange={() => toggleVariation(variation.id)}
                          className="mt-1 rounded border-border-strong text-primary focus:ring-primary"
                        />

                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          {variation.ad_text !== null && (
                            <EditableField
                              value={variation.ad_text || ''}
                              isEditing={editingField?.id === variation.id && editingField?.field === 'ad_text'}
                              editValue={editValue}
                              onEditValueChange={setEditValue}
                              onStartEdit={() => startEdit(variation.id, 'ad_text', variation.ad_text || '')}
                              onSave={saveEdit}
                              onCancel={() => setEditingField(null)}
                              onRegenerate={() => handleRegeneratePopupText(variation.id, 'ad_text')}
                              regenerating={regenerating.has(`${variation.id}-ad_text`)}
                              label="Title"
                              className="font-medium text-sm text-content"
                            />
                          )}

                          {/* Subtitle */}
                          {variation.ad_body !== null && (
                            <EditableField
                              value={variation.ad_body || ''}
                              isEditing={editingField?.id === variation.id && editingField?.field === 'ad_body'}
                              editValue={editValue}
                              onEditValueChange={setEditValue}
                              onStartEdit={() => startEdit(variation.id, 'ad_body', variation.ad_body || '')}
                              onSave={saveEdit}
                              onCancel={() => setEditingField(null)}
                              onRegenerate={() => handleRegeneratePopupText(variation.id, 'ad_body')}
                              regenerating={regenerating.has(`${variation.id}-ad_body`)}
                              label="Subtitle"
                              className="text-xs text-content-subtle mt-1"
                            />
                          )}
                        </div>
                      </div>

                      {/* Buttons row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Button 1 */}
                        {variation.cta_text !== null && (
                          <EditableField
                            value={variation.cta_text || ''}
                            isEditing={editingField?.id === variation.id && editingField?.field === 'cta_text'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit(variation.id, 'cta_text', variation.cta_text || '')}
                            onSave={saveEdit}
                            onCancel={() => setEditingField(null)}
                            onRegenerate={() => handleRegeneratePopupText(variation.id, 'cta_text')}
                            regenerating={regenerating.has(`${variation.id}-cta_text`)}
                            label="Button 1"
                            className="text-xs"
                            isCta
                          />
                        )}

                        {/* Button 2 */}
                        {variation.cta_text_2 !== null && (
                          <EditableField
                            value={variation.cta_text_2 || ''}
                            isEditing={editingField?.id === variation.id && editingField?.field === 'cta_text_2'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={() => startEdit(variation.id, 'cta_text_2', variation.cta_text_2 || '')}
                            onSave={saveEdit}
                            onCancel={() => setEditingField(null)}
                            onRegenerate={() => handleRegeneratePopupText(variation.id, 'cta_text_2')}
                            regenerating={regenerating.has(`${variation.id}-cta_text_2`)}
                            label="Button 2"
                            className="text-xs"
                            isSecondaryBtn
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          title={mode === 'popup' ? 'Create Popup Ads' : 'Create Custom Banner Ads'}
          onClose={() => { setShowCreateModal(false); setCreateResult(null); }}
          size="md"
        >
          {createResult ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-success-soft rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-content mb-1">
                {createResult.created} Ads Created
              </h3>
              <p className="text-sm text-content-subtle mb-4">
                {mode === 'popup' ? 'Popup' : 'Custom Banner'} ads have been created and are ready to serve
              </p>
              <Button onClick={() => { window.location.href = '/ads'; }}>
                Go to Ads
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Campaign</label>
                <Select
                  value={campaignId}
                  onChange={(v) => setCampaignId(v)}
                  placeholder="Select campaign..."
                  searchable
                  options={[
                    { value: '', label: 'Select campaign...' },
                    ...(campaigns?.map((c: any) => ({ value: c.id, label: c.name })) || []),
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Click URL</label>
                <div className="flex items-start gap-2">
                  <input
                    type="url"
                    value={clickUrl}
                    onChange={e => setClickUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                  />
                  <div className="w-56 shrink-0">
                    <TrackerLinkPicker onPick={setClickUrl} />
                  </div>
                </div>
              </div>

              {mode === 'popup' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-1">
                      Popup Delay: {popupDelaySec}s
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={3600}
                      value={popupDelaySec}
                      onChange={e => setPopupDelaySec(e.target.value)}
                      className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-muted mb-1">
                      Repeat every: {popupRepeatSec || '—'}s
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={86400}
                      value={popupRepeatSec}
                      onChange={e => setPopupRepeatSec(e.target.value)}
                      className="w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                </>
              )}

              <div className="bg-surface-muted rounded-md p-3 text-sm text-content-muted">
                <p>Will create <strong>{selectedVariations.size}</strong> {mode === 'popup' ? 'popup' : 'custom banner'} ads</p>
                <p>Targeting: {[...new Set((mode === 'popup' ? popupVariations : variations).filter(v => selectedVariations.has(v.id)).flatMap(v => v.geos))].join(', ')}</p>
                <p>Platforms: {platforms.join(', ')}</p>
                {mode === 'popup' && <p>Delay: {popupDelaySec}s</p>}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="secondary"
                  onClick={() => { setShowCreateModal(false); setCreateResult(null); }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={mode === 'popup' ? handlePopupBulkCreate : handleBulkCreate}
                  disabled={creating || !campaignId || !clickUrl}
                >
                  {creating ? 'Creating...' : `Create ${selectedVariations.size} Ads`}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// Helper to map step to number
function steps(step: Step): number {
  return step === 'config' ? 0 : step === 'preview' ? 1 : 2;
}

// Editable field component
function EditableField({
  value,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  onRegenerate,
  regenerating,
  label: _label,
  className,
  isCta,
  isSecondaryBtn,
}: {
  value: string;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (v: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRegenerate: () => void;
  regenerating: boolean;
  label: string;
  className?: string;
  isCta?: boolean;
  isSecondaryBtn?: boolean;
}) {
  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={editValue}
          onChange={e => onEditValueChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') onSave();
            if (e.key === 'Escape') onCancel();
          }}
          className="flex-1 px-2 py-1 border border-primary/40 rounded text-sm focus:ring-primary focus:border-primary"
          autoFocus
        />
        <button onClick={onSave} className="text-success hover:text-success text-xs p-1">Save</button>
        <button onClick={onCancel} className="text-content-subtle hover:text-content-muted text-xs p-1">Cancel</button>
      </div>
    );
  }

  if (isSecondaryBtn) {
    return (
      <div className="flex items-center gap-2 group">
        <span className="px-3 py-1 bg-surface-muted text-content-muted text-xs rounded-md">{value}</span>
        <button
          onClick={onStartEdit}
          className="text-content-subtle hover:text-content-subtle opacity-0 group-hover:opacity-100 transition-opacity"
          title="Edit"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          onClick={onRegenerate}
          disabled={regenerating}
          className="text-content-subtle hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
          title="Regenerate"
        >
          {regenerating ? (
            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  if (isCta) {
    return (
      <div className="flex items-center gap-2 group">
        <span className="px-3 py-1 bg-primary text-white text-xs rounded-md">{value}</span>
        <button
          onClick={onStartEdit}
          className="text-content-subtle hover:text-content-subtle opacity-0 group-hover:opacity-100 transition-opacity"
          title="Edit"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          onClick={onRegenerate}
          disabled={regenerating}
          className="text-content-subtle hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
          title="Regenerate"
        >
          {regenerating ? (
            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-start gap-1 ${className || ''}`}>
      <span className="flex-1">{value}</span>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button
          onClick={onStartEdit}
          className="text-content-subtle hover:text-content-subtle p-0.5"
          title="Edit"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          onClick={onRegenerate}
          disabled={regenerating}
          className="text-content-subtle hover:text-primary p-0.5"
          title="Regenerate"
        >
          {regenerating ? (
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
